import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PropertyService } from '../../properties/service/property.service';
import { DialogModule } from 'primeng/dialog';
import { SliderModule } from 'primeng/slider';
import { SidebarModule } from 'primeng/sidebar';
import { ToastModule } from 'primeng/toast';
import { Unit } from 'src/app/core/shared/interface';
import { UnitsFormComponent } from '../units-form/units-form.component';
import { AvailabilityStatus, AvailabilityStatusText, VisibilityStatus, VisibilityStatusText } from '../../../enums/units.enum';
import { CheckboxModule } from 'primeng/checkbox';
import { WaitlistService } from '../../../core/shared/waitlist.service';
import { Galleria, GalleriaModule } from 'primeng/galleria';
interface FloorPlanFilter {
  _id: string;
  name: string;
}
@Component({
  selector: 'app-units-listing',
  standalone: true,
  imports: [ToastModule, TableModule, UnitsFormComponent, SidebarModule, ButtonModule, CommonModule, DialogModule, DropdownModule, FormsModule, CalendarModule, SliderModule, CheckboxModule, GalleriaModule],
  templateUrl: './units-listing.component.html',
  styleUrl: './units-listing.component.scss',
  providers: [MessageService],
})
export class UnitsListingComponent implements OnInit {
  today: Date = new Date();
  type: string = '';
  propertyId = "6724ec85459e3cdf8db55149";
  display: boolean = false;
  position: 'center' | 'right' = 'center';
  visible: boolean = false;
  units: Unit[] = [];
  totalRecords: number = 0;
  rowsPerPage: number = 10;
  currentPage: number = 0;
  filters: any = {
    unit: '',
    beds: '',
    baths: '',
    sq_ft: '',
    search: '',
    visibility_date: null,
    waitlist: "all",
    floorplan: ''
  };
  sorting: any = {
    field: 'unit',
    order: 1
  };
  isSidebarVisible: boolean = false;
  selectedUnit: Unit = {} as Unit;
  
  allPlanNames: { label: any; value: any; }[] = [];
  allBedOptions: { label: any; value: any; }[] = [];
  allBathOptions: { label: any; value: any; }[] = [];
  allSqftOptions: { label: any; value: any; }[] = [];
  allFloorplanNames: { label: any; value: any; }[] = [];
  blockSpecialCharsPattern = /[.*+?^${}()|[\]\\]/g;
  AvailabilityStatus = AvailabilityStatus;
  AvailabilityStatusText = AvailabilityStatusText;
  VisibilityStatus = VisibilityStatus;
  VisibilityStatusText = VisibilityStatusText;
  availabilityDisabled: boolean = false;
  visibilityDisabled: boolean = false;
  bulkActions = [
    { label: "Change Availability", value: "availability" },
    { label: "Change Visibility", value: "visibility" }
  ];
  selectedUnits: Unit[] = [];
  selectAll: boolean = false;
  bulkSidebarVisible = false;
  bulk: any = {
    availability_status: '',
    availablity_date: null,
    visibility_status: '',
    visibility_date: null
  };
  selectedBulkAction = '';
  availabilityDialogVisible: boolean = false;
  visibilityDialogVisible: boolean = false;
  minDate: Date = new Date();
  maxVisibilityDate: Date | null = null;
  waitlistOptions = [
    { label: "All Units", value: "all" },
    { label: "Available Units", value: false },
    { label: "Waitlist Units", value: true }
  ];
  isWaitListSidebarVisible: boolean = false;
  waitlistUsers: any[] = [];
  propTimeZone: string = '';
  mediaFiles: { url: string, type: 'image' | 'video' }[] = [];
  showGalleria: boolean = false;
  activeIndex: number = 0;
  pendingGalleriaIndex: number = 0;
  constructor(
    private propertyService: PropertyService, 
    private messageService: MessageService,
    private waitlistService: WaitlistService,
  ) { }

  ngOnInit(): void {
    //this.fetchAllUnits();
    this.propertyService.getUserInfo().subscribe(user => {
        this.propertyId = user.property_id;
        this.propTimeZone = user.property.timezone;

        this.loadUnitFilters(this.propertyId);
    });
  }

  showSuccess(message: string) {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: message });
  }

  showError(message: string) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }

  loadUnitFilters(propertyId: string) {
    this.propertyService.getAllUnitFilters(propertyId).subscribe(
      (response: any) => {
        //this.allPlanNames = this.mapToDropdown<string>(response.units);
        this.allBedOptions = this.mapToDropdown<number>(response.beds);
        this.allBathOptions = this.mapToDropdown<number>(response.baths);
        this.allSqftOptions = this.mapToDropdown<number>(response.sq_ft);
      },
      (error) => {
        console.error('Error loading unit filters:', error);
      }
    );

    // Load floor plan names for plan dropdown
    this.propertyService.getFloorPlanFilters(this.propertyId).subscribe(
      (response: any) => {
        this.allFloorplanNames = response.floorPlans.map((fp: FloorPlanFilter) => ({
          label: fp.name,
          value: fp._id
        }));
      },
      (error) => {
        console.error('Error loading floor plan filters:', error);
      }
    );
  }

  private mapToDropdown<T>(arr: T[]): { label: T; value: T }[] {
    return arr.filter(v => v !== undefined && v !== null && v !== '').map(v => ({ label: v, value: v }));
  }

  fetchAllUnits(): void {
    this.propertyService.getAllUnits(this.propertyId, {}).subscribe({
      next: (response: { data: Unit[]; total: number; }) => {
        const units = response.data || response;
        this.allPlanNames = [...new Set(units.map((item: any) => item.unit))].map(plan => ({
          label: plan,
          value: plan
        }));
        this.allBedOptions = [...new Set(units.map((item: any) => item.beds))].map(beds => ({
          label: beds,
          value: beds
        }));
        this.allBathOptions = [...new Set(units.map((item: any) => item.baths))].map(baths => ({
          label: baths,
          value: baths
        }));
        this.allSqftOptions = [...new Set(units.map((item: any) => item.sq_ft))].map(sqft => ({
          label: sqft,
          value: sqft
        }));
      },
      error: () => {
        console.error('Error fetching units');
      }
    });
  }

  getAllUnits(event: any): void {

    const page = Math.floor(event.first / event.rows) + 1;
    const limit = event.rows;
    const filtersPayload: any = { ...this.filters };
    if (
      Array.isArray(this.filters?.visibility_date) &&
      this.filters.visibility_date.length === 2 &&
      this.filters.visibility_date.every(Boolean)
    ) {
      const [start, end] = this.filters.visibility_date;
      filtersPayload.visibility_date = [start.toISOString(), end.toISOString()];
    } else {
      delete filtersPayload.visibility_date;
    }

    const queryParams = {
      page,
      limit,
      sortField: event.sortField || 'unit',
      sortOrder: event.sortOrder || 1,
      filters: JSON.stringify(filtersPayload)
    };
    this.propertyService.getAllUnits(this.propertyId, queryParams).subscribe({
      next: (response: { data: Unit[]; total: number; }) => {
        this.units = response.data.map(unit => ({
          ...unit,
          availablity_date: unit?.availablity_date ? new Date(unit?.availablity_date) : '',
          isSelected: !!unit.isSelected
        }));
        this.totalRecords = response.total;
        this.currentPage = page - 1;
      },
      error: () => {
        console.error('Error fetching units');
      }
    });
  }

  onFilterChange(): void {
    this.getAllUnits({
      first: this.currentPage * this.rowsPerPage,
      rows: this.rowsPerPage,
      sortField: this.sorting.field,
      sortOrder: this.sorting.order
    });
  }

  resetFilters(): void {
    this.filters = {
      unit: '',
      beds: '',
      baths: '',
      sq_ft: '',
      search: '',
      visibility_date: null,
      waitlist: "all",
      floorplan: '',
    };
    this.onFilterChange();
  }

  openCreateSidebar(): void {
    this.selectedUnit = { property_id: this.propertyId } as Unit;
    this.isSidebarVisible = true;
    this.type = 'create';
  }

  editUnit(unit: Unit): void {
    this.selectedUnit = {
      ...unit,
      availablity_date: unit?.availablity_date ? new Date(unit.availablity_date) : '',
      visibility_date: unit?.visibility_date ? new Date(unit.visibility_date) : '',
      images: Array.isArray(unit?.images) ? [...unit.images] : [],
      videos: Array.isArray(unit?.videos) ? [...unit.videos] : [],
    };
    this.isSidebarVisible = true;
    this.type = 'edit';

    this.propertyService.getUnitById(unit._id).subscribe({
      next: (response: Unit) => {
        this.selectedUnit = {
          ...response,
          availablity_date: response?.availablity_date ? new Date(response.availablity_date) : '',
          visibility_date: response?.visibility_date ? new Date(response.visibility_date) : '',
          images: Array.isArray(response?.images) ? [...response.images] : this.selectedUnit.images,
          videos: Array.isArray(response?.videos) ? [...response.videos] : this.selectedUnit.videos,
        };
      },
      error: () => {
        this.showError('Unable to load unit media');
      }
    });
  }

  onFormSubmit(unitFormData: FormData): void {
    if (this.selectedUnit._id) {
      this.propertyService.updateUnit(this.selectedUnit._id, unitFormData).subscribe(() => {
        this.showSuccess('Unit updated successfully');
        this.getAllUnits({ first: this.currentPage * this.rowsPerPage, rows: this.rowsPerPage });
      });
    } else {
      this.propertyService.createUnit(unitFormData).subscribe(() => {
        this.showSuccess('Unit Created successfully');
        this.getAllUnits({ first: this.currentPage * this.rowsPerPage, rows: this.rowsPerPage });
      });
    }
    this.isSidebarVisible = false;
  }

  confirmDelete(unit: any): void {
    this.selectedUnit = unit;
    this.display = true;
  }

  onCancel(): void {
    this.display = false;
    this.selectedUnit = {} as Unit;
  }

  onConfirm(): void {
    if (this.selectedUnit) {
      this.propertyService.deleteUnit(this.selectedUnit._id).subscribe(() => {
        this.showSuccess('Unit deleted successfully!');
        this.getAllUnits({ first: this.currentPage * this.rowsPerPage, rows: this.rowsPerPage });
        this.display = false;
        this.selectedUnit = {} as Unit;
      });
    }
  }

  // Block on typing
  blockSpecialChars(event: KeyboardEvent) {
    console.log("BLOCK typing");
    if (this.blockSpecialCharsPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  // Block on paste
  blockSpecialCharsOnPaste(event: ClipboardEvent) {
    console.log("BLOCK PASTE");
    const pasteData = event.clipboardData?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(pasteData)) {
      event.preventDefault();
    }
  }

  // Block on drop
  blockSpecialCharsOnDrop(event: DragEvent) {
    console.log("BLOCK Drop");
    const dropData = event.dataTransfer?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(dropData)) {
      event.preventDefault();
    }
  }

  // Final cleanup in case something sneaks in
  sanitizeInput(event: Event) {
    console.log("SANITIZE INPUT");
    const input = event.target as HTMLInputElement;
    if (this.blockSpecialCharsPattern.test(input.value)) {
      input.value = input.value.replace(this.blockSpecialCharsPattern, '');
    }
  }

  getAvailabilityClass(unit: Unit): string {
    switch (unit.availability_status) {
      case this.AvailabilityStatus.NOTAVAILABLE:
        return 'statusCancelled';
      case this.AvailabilityStatus.AVAILABLENOW || (unit.availablity_date && new Date(unit.availablity_date) <= this.today):
        return 'statusActive';
      case this.AvailabilityStatus.DATE:
        if(!unit.availablity_date) {
          return 'statusCancelled';
        } else if (unit.availablity_date && new Date(unit.availablity_date) <= this.today) {
          return 'statusActive';
        } else {
          return 'statusPending';
        }
      default:
        return 'statusPending';
    }
  }

  onRowSelect() {
    this.selectedUnits = this.units.filter(u => u.isSelected);
  }

  toggleSelectAll() {
    if (!this.units || !Array.isArray(this.units)) return;
    this.units.forEach(u => (u.isSelected = this.selectAll));
    this.onRowSelect();
  }

  // Utility to refresh table after bulk update
  refreshUnits() {
    this.getAllUnits({
      first: this.currentPage * this.rowsPerPage,
      rows: this.rowsPerPage,
      sortField: this.sorting.field,
      sortOrder: this.sorting.order
    });

    // Reset selection
    this.units.forEach(u => u.isSelected = false);
    this.selectedUnits = [];
    this.selectAll = false;
  }

  openAvailabilityDialog() {
    if (!this.selectedUnits.length) {
      this.showError("Select at least one unit");
      return;
    }
    this.availabilityDialogVisible = true;
  }

  openVisibilityDialog() {
    if (!this.selectedUnits.length) {
      this.showError("Select at least one unit");
      return;
    }
    this.visibilityDialogVisible = true;
  }

  onAvailabilityChange(event: any) {
    const selectedValue = event.target.value;
    this.applyAvailabilityStatusLogic(selectedValue);
  }

  applyAvailabilityStatusLogic(selectedValue: string) {
    if (selectedValue === AvailabilityStatus.DATE) {
      this.availabilityDisabled = false;
      // keep date as is or null
    }
    else if (selectedValue === AvailabilityStatus.AVAILABLENOW) {
      this.bulk.availablity_date = this.today;
      this.availabilityDisabled = true;
    }
    else if (selectedValue === AvailabilityStatus.NOTAVAILABLE) {
      this.bulk.availablity_date = null;
      this.availabilityDisabled = true;
    }
  }

  updateVisibilityMinDate() {
    if (this.bulk.availablity_date) {
      this.maxVisibilityDate = new Date(this.bulk.availablity_date);
    } else {
      this.maxVisibilityDate = null;
    }
  }

  onVisibilityChange(event: any) {
    const selectedValue = event.target.value;
    this.applyVisibilityStatusLogic(selectedValue);
  }

  applyVisibilityStatusLogic(selectedValue: string) {
    if (selectedValue === VisibilityStatus.DATE) {
      this.visibilityDisabled = false;
    }
    else if (selectedValue === VisibilityStatus.VISIBLENOW) {
      this.bulk.visibility_date = this.today;
      this.visibilityDisabled = true;
    }
    else if (selectedValue === VisibilityStatus.NOTVISIBLE) {
      this.bulk.visibility_date = null;
      this.visibilityDisabled = true;
    }
  }

  applyBulkAvailability() {
    const payload = {
      unit_ids: this.selectedUnits.map(u => u._id),
      availability_status: this.bulk.availability_status,
      availablity_date: this.bulk.availablity_date
    };
    this.propertyService.bulkUpdate(payload).subscribe(() => {
      this.showSuccess("Availability updated successfully");
      this.availabilityDialogVisible = false;
      this.refreshUnits();
    });
  }

  applyBulkVisibility() {
    const payload = {
      unit_ids: this.selectedUnits.map(u => u._id),
      visibility_status: this.bulk.visibility_status,
      visibility_date: this.bulk.visibility_date
    };
    this.propertyService.bulkUpdate(payload).subscribe(() => {
      this.showSuccess("Visibility updated successfully");
      this.visibilityDialogVisible = false;
      this.refreshUnits();
    });
  }

  applyBulkUpdate(type: 'availability' | 'visibility') {
    const payload: any = { unit_ids: this.selectedUnits.map(u => u._id) };

    if (type === 'availability') {
      payload.availability_status = this.bulk.availability_status;
      payload.availablity_date = this.bulk.availablity_date;
    } else if (type === 'visibility') {
      payload.visibility_status = this.bulk.visibility_status;
      payload.visibility_date = this.bulk.visibility_date;
    }

    this.propertyService.bulkUpdate(payload).subscribe(() => {
      this.showSuccess(
        `${type === 'availability' ? 'Availability' : 'Visibility'} updated successfully`
      );
      if (type === 'availability') this.availabilityDialogVisible = false;
      if (type === 'visibility') this.visibilityDialogVisible = false;
      this.refreshUnits();
    });
  }
  onWaitlistClick(unit: any) {
    this.waitlistService.getWaitlistByTarget('unit', unit._id).subscribe(
      (res) => {
        this.waitlistUsers = res;
        this.isWaitListSidebarVisible = true;
      },
      (err) => console.error(err)
    );
  }
  closeSidebar() {
    this.isWaitListSidebarVisible = false;
    this.waitlistUsers = [];
    this.selectedUnit = {} as Unit;
  }

  openMediaLightbox(rawMediaList: any[], index: number = 0) {
    this.mediaFiles = rawMediaList.map(media => ({
      url: media.uri || media.url || media.src,
      type: media.type,
    }));
    this.pendingGalleriaIndex = Math.max(0, Math.min(index, this.mediaFiles.length - 1));
    this.activeIndex = this.pendingGalleriaIndex;
    this.showGalleria = true;

    // PrimeNG galleria can reset activeIndex during open; re-apply once overlay is mounted.
    setTimeout(() => {
      this.activeIndex = this.pendingGalleriaIndex;
    });

    setTimeout(() => {
      this.activeIndex = this.pendingGalleriaIndex;
    }, 60);
  }

  onVisibleChange(isVisible: boolean) {
    if (isVisible) {
      setTimeout(() => {
        this.activeIndex = this.pendingGalleriaIndex;
      });
      return;
    }

    if (!isVisible) {
      setTimeout(() => {
        document.body.classList.add('p‑overflow‑hidden');
      }, 50);
    }
  }
}
